import ChatService from '../../services/ChatService'

const state = () => ({
    loadingChats: false,
    sendingChatMessage: false,
    chat: null,
    chatUser: {
        send: false,
        username: "",
        email: "",
        message: ""
    },
    // Janela de 24h da conversa aberta (regra da Meta para forma livre)
    conversationWindow: {
        open: false,
        lastInboundAt: null,
        expiresAt: null,
        remainingMs: 0,
        loaded: false,
    },
})

const mutations = {
    SET_CHAT: (state, payload) => {
        state.chat = payload
    },
    SET_CHAT_USER: (state, payload) => {
        state.chatUser = payload
    },
    SET_LOADING_CHATS: (state, payload) => {
        state.loadingChat = payload
    },
    SET_SENDING_CHAT_MESSAGE: (state, payload) => {
        state.sendingChatMessage = payload
    },
    SET_RESTART_MESSAGE: (state) => {
        state.chatUser.message = ""
    },
    SET_CONVERSATION_WINDOW: (state, payload) => {
        state.conversationWindow = { ...payload, loaded: true }
    },
    RESET_CONVERSATION_WINDOW: (state) => {
        state.conversationWindow = { open: false, lastInboundAt: null, expiresAt: null, remainingMs: 0, loaded: false }
    },
}

// Mensagem temporária exibida na conversa enquanto o envio confirma (optimistic UI).
// O formato imita o retorno do backend para o renderizador do chat funcionar igual.
const buildPendingMessage = ({ phone, text, media = null, templateName = null }) => {
    const mediaKind = media
        ? (media.type?.startsWith('image/') ? 'image'
            : media.type?.startsWith('video/') ? 'video'
            : media.type?.startsWith('audio/') ? 'audio'
            : 'document')
        : null;
    return {
        id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: 'sent',
        status: 'pending',
        createdAt: new Date().toISOString(),
        recipientId: phone,
        phoneNumber: phone,
        templateName,
        mediaUrl: media ? URL.createObjectURL(media) : null,
        components: [
            ...(mediaKind ? [{ type: mediaKind }] : []),
            ...(text ? [{ type: 'body', parameters: [{ type: 'text', text }] }] : []),
        ],
    };
};

const actions = {
    // Envio via TEMPLATE (janela fechada) — a mensagem aparece na hora (otimista)
    async createChat({commit, dispatch}, {user, message, phoneNumber}){
        const phone = user?.phone_wa || phoneNumber;
        const pending = buildPendingMessage({ phone, text: message, templateName: 'simple' });
        commit('ADD_PENDING_MESSAGE', pending)
        commit('SET_RESTART_MESSAGE')
        try{
            const response = await ChatService.create({phone, message});
            commit('RESOLVE_PENDING_MESSAGE', { tempId: pending.id, message: response.data?.item || null })
            if (!response.data?.item) {
                // fallback: servidor não retornou a mensagem criada — sincroniza
                if (user?.id) {
                    await dispatch('fetchMessages', {pageNumber: 1, UserId: user.id}, { root: true });
                } else if (phoneNumber) {
                    await dispatch('fetchMessages', {pageNumber: 1, phoneNumber}, { root: true });
                }
            }
        } catch (error) {
            commit('RESOLVE_PENDING_MESSAGE', { tempId: pending.id, status: 'failed' })
            commit('SET_ALERT', {
                heading: 'error',
                message: error.response?.data?.message || 'Erro ao enviar mensagem'
            });
        }
    },

    // Envio de FORMA LIVRE (texto/mídia) — exige janela de 24h aberta; otimista
    async sendChatFreeform({commit, dispatch}, {user, phoneNumber, text, media = null, kind = 'text', replyToWAId = null}){
        const phone = user?.phone_wa || phoneNumber;
        const pending = buildPendingMessage({ phone, text, media });
        commit('ADD_PENDING_MESSAGE', pending)
        commit('SET_RESTART_MESSAGE')
        try{
            commit('SET_SENDING_CHAT_MESSAGE', true)
            const response = await ChatService.sendFreeform({ to: phone, kind, text, media, replyToWAId });
            commit('RESOLVE_PENDING_MESSAGE', { tempId: pending.id, message: response.data?.item || null })
            if (!response.data?.item) {
                if (user?.id) {
                    await dispatch('fetchMessages', {pageNumber: 1, UserId: user.id}, { root: true });
                } else if (phoneNumber) {
                    await dispatch('fetchMessages', {pageNumber: 1, phoneNumber}, { root: true });
                }
            }
            return true;
        } catch (error) {
            commit('RESOLVE_PENDING_MESSAGE', { tempId: pending.id, status: 'failed' })
            const code = error.response?.data?.code;
            if (code === 'WINDOW_CLOSED') {
                // janela expirou entre a checagem e o envio — atualiza o estado
                await dispatch('fetchConversationWindow', { phone: user?.phone_wa || phoneNumber });
            }
            commit('SET_ALERT', {
                heading: 'error',
                message: error.response?.data?.message || error.response?.data?.error || 'Erro ao enviar mensagem'
            });
            return false;
        } finally {
            commit('SET_SENDING_CHAT_MESSAGE', false)
        }
    },

    // Consulta a janela de 24h da conversa
    async fetchConversationWindow({commit}, { phone }){
        if (!phone) {
            commit('RESET_CONVERSATION_WINDOW');
            return;
        }
        try{
            const response = await ChatService.getConversationWindow(phone);
            commit('SET_CONVERSATION_WINDOW', response.data)
        } catch (error) {
            console.error('Falha ao consultar a janela da conversa:', error);
            commit('RESET_CONVERSATION_WINDOW');
        }
    },

    // Envio de mensagem INTERATIVA (botões/lista/CTA) — janela aberta; otimista
    async sendChatInteractive({commit, dispatch}, { phoneNumber, interactive, replyToWAId = null }){
        const pending = buildPendingMessage({ phone: phoneNumber, text: interactive.bodyText });
        commit('ADD_PENDING_MESSAGE', pending)
        try{
            commit('SET_SENDING_CHAT_MESSAGE', true)
            const response = await ChatService.sendInteractive({ to: phoneNumber, interactive, replyToWAId });
            commit('RESOLVE_PENDING_MESSAGE', { tempId: pending.id, message: response.data?.item || null })
            return true;
        } catch (error) {
            commit('RESOLVE_PENDING_MESSAGE', { tempId: pending.id, status: 'failed' })
            if (error.response?.data?.code === 'WINDOW_CLOSED') {
                await dispatch('fetchConversationWindow', { phone: phoneNumber });
            }
            commit('SET_ALERT', {
                heading: 'error',
                message: error.response?.data?.errors?.join(' | ') || error.response?.data?.message || 'Erro ao enviar a mensagem interativa'
            });
            return false;
        } finally {
            commit('SET_SENDING_CHAT_MESSAGE', false)
        }
    },

    // Envio de TEMPLATE aprovado pelo nome (funciona com a janela fechada); otimista.
    // O pending NÃO leva templateName: o previewText já vem com as variáveis
    // substituídas — com templateName o renderer substituiria de novo (duplicado).
    async sendChatTemplate({commit}, { phoneNumber, templateName, language = null, values = {}, previewText = '' }){
        const pending = buildPendingMessage({ phone: phoneNumber, text: previewText || `Template: ${templateName}` });
        commit('ADD_PENDING_MESSAGE', pending)
        try{
            commit('SET_SENDING_CHAT_MESSAGE', true)
            const response = await ChatService.sendTemplate({ to: phoneNumber, templateName, language, values });
            commit('RESOLVE_PENDING_MESSAGE', { tempId: pending.id, message: response.data?.item || null })
            return true;
        } catch (error) {
            commit('RESOLVE_PENDING_MESSAGE', { tempId: pending.id, status: 'failed' })
            commit('SET_ALERT', {
                heading: 'error',
                message: error.response?.data?.errors?.join(' | ') || error.response?.data?.message || 'Erro ao enviar o template'
            });
            return false;
        } finally {
            commit('SET_SENDING_CHAT_MESSAGE', false)
        }
    },

    // "Digitando..." — fire-and-forget, nunca alerta
    async sendChatTyping(_, { phone }){
        if (!phone) return;
        try { await ChatService.sendTyping(phone); } catch (e) { /* cosmético */ }
    },

    // Marca a conversa como lida: zera o contador local na hora e avisa o
    // backend (que enfileira o mark-as-read na Meta). Silencioso em caso de erro.
    async markConversationRead({commit, dispatch}, { phone }){
        if (!phone) return;
        commit('CLEAR_CONVERSATION_UNREAD', phone)
        try {
            await ChatService.markConversationRead(phone);
        } catch (error) {
            console.error('Falha ao marcar conversa como lida:', error);
        } finally {
            dispatch('fetchUnreadSummary', null, { root: true });
        }
    },

    // Envia reação (emoji) a uma mensagem da conversa; emoji vazio remove
    async sendChatReaction({commit}, { to, targetWAId, emoji = '' }){
        try {
            const response = await ChatService.sendReaction({ to, targetWAId, emoji });
            if (response.data?.item) {
                commit('UPDATE_REALTIME_MESSAGE', { id: response.data.item.id, WAId: targetWAId, message: response.data.item })
            }
            return true;
        } catch (error) {
            commit('SET_ALERT', {
                heading: 'error',
                message: error.response?.data?.message || 'Erro ao enviar a reação'
            });
            return false;
        }
    },

    async restartChat({commit, state}){
        const newChatUser = {
            username: state.chatUser.username,
            email: state.chatUser.email,
            message: "",
            send: false
        }
        commit('SET_CHAT_USER', newChatUser)
    },
}

const getters = {
    getChat: state => state.chat,
    getChatUser: state => state.chatUser,
    getLoadingChat: state => state.loadingChat,
    getSendingChatMessage: state => state.sendingChatMessage,
    getConversationWindow: state => state.conversationWindow,
}

export default {
    state,
    getters,
    mutations,
    actions
};
